import type { Editor } from '@tiptap/core';
import { getSelectionRanges, NodeRangeSelection } from '@tiptap/extension-node-range';
import type { Node } from '@tiptap/pm/model';
import { type SelectionRange, NodeSelection } from '@tiptap/pm/state';

import { toTitleCase } from '@/lib/utils';
import type { NormalizedNestedOptions } from '../types/options.js';
import { findElementNextToCoords } from './findNextElementFromCursor.js';
import { removeNode } from './removeNode.js';

/**
 * Get the correct label to display for the node while dragging.
 * If the node has a subtype, use the subtype label. Otherwise, use the node type label.
 * @param node The node to get the label for.
 * @returns The label to display for the node.
 */
function getDragLabel(node: Node): string {
    const name = node.type.name;

    switch (name) {
        case 'zyExample': {
            const typeMap: Record<string, string> = {
                example: 'zyExample',
                figure: 'zyFigure',
                enum: 'zyEnum',
                table: 'zyTable',
                try: 'zyTry',
            };
            return typeMap[node.attrs.type] ?? 'zyExample';
        }

        case 'questionSet': {
            let qType = node.attrs.questionType;
            if (!qType) {
                for (let i = 0; i < node.childCount; i++) {
                    const childName = node.child(i).type.name;
                    if (['multipleChoice', 'shortAnswer', 'matching'].includes(childName)) {
                        qType = childName;
                        break;
                    }
                }
            }
            const qTypeMap: Record<string, string> = {
                multipleChoice: 'Multiple Choice',
                shortAnswer: 'Short Answer',
                matching: 'Matching',
            };
            return qTypeMap[qType] ?? 'Activity';
        }

        default:
            return toTitleCase(name);
    }
}
export interface DragContext {
    node: Node | null;
    pos: number;
}

function getDragHandleRanges(
    event: DragEvent,
    editor: Editor,
    nestedOptions?: NormalizedNestedOptions,
    dragContext?: DragContext,
): SelectionRange[] {
    const { doc } = editor.view.state;

    // In nested mode with known context, use the pre-calculated position
    // This prevents recalculation issues when mouse position shifts during drag start
    if (nestedOptions?.enabled && dragContext?.node && dragContext.pos >= 0) {
        const nodeStart = dragContext.pos;
        const nodeEnd = dragContext.pos + dragContext.node.nodeSize;

        return [
            {
                $from: doc.resolve(nodeStart),
                $to: doc.resolve(nodeEnd),
            },
        ];
    }

    // Fallback: recalculate from mouse position (used in non-nested mode)
    const result = findElementNextToCoords({
        editor,
        x: event.clientX,
        y: event.clientY,
        direction: 'right',
        nestedOptions,
    });

    if (!result.resultNode || result.pos === null) {
        return [];
    }

    // For non-nested mode, use depth 0 to select the outermost block
    const $from = doc.resolve(result.pos);
    const $to = doc.resolve(result.pos + result.resultNode.nodeSize);

    return getSelectionRanges($from, $to, 0);
}

export function dragHandler(event: DragEvent, editor: Editor, nestedOptions?: NormalizedNestedOptions, dragContext?: DragContext) {
    const { view } = editor;

    if (!event.dataTransfer) {
        return;
    }

    const { empty, $from, $to } = view.state.selection;

    const dragHandleRanges = getDragHandleRanges(event, editor, nestedOptions, dragContext);

    const selectionRanges = getSelectionRanges($from, $to, 0);
    const isDragHandleWithinSelection = selectionRanges.some(range => {
        return dragHandleRanges.find(dragHandleRange => {
            return dragHandleRange.$from === range.$from && dragHandleRange.$to === range.$to;
        });
    });

    const ranges = empty || !isDragHandleWithinSelection ? dragHandleRanges : selectionRanges;

    if (!ranges.length) {
        return;
    }

    const { tr } = view.state;
    const wrapper = document.createElement('div');
    const from = ranges[0].$from.pos;
    const to = ranges[ranges.length - 1].$to.pos;

    // For nested mode, create slice directly to avoid NodeRangeSelection expanding to parent
    const isNestedDrag = nestedOptions?.enabled && dragContext?.node;

    let slice;
    let selection;

    if (isNestedDrag) {
        // Create slice directly from the exact positions
        slice = view.state.doc.slice(from, to);

        // Use NodeSelection for nested mode to select exactly the target node
        // NodeRangeSelection would expand to the parent
        selection = NodeSelection.create(view.state.doc, from);
    } else {
        selection = NodeRangeSelection.create(view.state.doc, from, to);
        slice = selection.content();
    }

    // We don't want to clone the elements here to avoid performance issues
    // ranges.forEach(range => {
    //   const element = view.nodeDOM(range.$from.pos) as HTMLElement
    //   const clonedElement = cloneElement(element)

    //   wrapper.append(clonedElement)
    // })

    // Render a small div with the node type and caption while dragging
    ranges.forEach(range => {
        const node = view.state.doc.nodeAt(range.$from.pos);
        if (node) {
            const nodeLabel = getDragLabel(node);
            const nodeCaption = node.attrs.caption || '';

            // Only show the colon and caption if caption exists
            const labelText = nodeLabel + (nodeCaption ? `: ${nodeCaption}` : '');
            const label = document.createElement('div');
            label.textContent = labelText;
            label.style.padding = '4px 8px';
            label.style.background = '#fff';
            label.style.border = '1px solid #ccc';
            label.style.borderRadius = '4px';
            label.style.fontSize = '13px';
            wrapper.append(label);
        }
    });

    wrapper.style.position = 'absolute';
    wrapper.style.top = '-10000px';
    document.body.append(wrapper);

    event.dataTransfer.clearData();
    event.dataTransfer.setDragImage(wrapper, 0, 0);

    // tell ProseMirror the dragged content
    view.dragging = { slice, move: true };

    tr.setSelection(selection);

    view.dispatch(tr);

    // clean up
    document.addEventListener('drop', () => removeNode(wrapper), { once: true });
}
